/**
 * aiPreview.logic — чистая логика AI-превью наряда (без React).
 *
 * P1 Enterprise Hardening:
 *  - маппинг item_type → kind для ReviewGate
 *  - сбор конфликтов физической валидации (physical_status)
 *  - пересчёт has_unresolved после inline-резолва стойки/каталога/порта
 */
import type { WorkOrderItemPreview } from '../../types/workOrder'

export type ReviewKind = 'install' | 'remove' | 'patch' | 'unpatch' | 'custom'

/** Статусы физической валидации, блокирующие создание наряда. */
export const BLOCKING_PHYSICAL_STATUSES = [
  'COLLISION',
  'OUT_OF_BOUNDS',
  'RESOURCE_CLAIMED',
  'NOT_FOUND',
  'PORT_BUSY',
] as const

const ITEM_KIND: Record<string, ReviewKind> = {
  INSTALL_EQUIPMENT: 'install',
  REMOVE_EQUIPMENT: 'remove',
  PATCH_CABLE: 'patch',
  UNPATCH_CABLE: 'unpatch',
  MOVE_EQUIPMENT: 'custom',
  MOVE_RACK: 'custom',
  CUSTOM: 'custom',
}

export function itemKind(itemType: string): ReviewKind {
  return ITEM_KIND[itemType] ?? 'custom'
}

export function physicalStatus(item: WorkOrderItemPreview): string | undefined {
  return (item.metadata?.physical_status as string | undefined) ?? undefined
}

export function isPhysicallyBlocked(item: WorkOrderItemPreview): boolean {
  const st = physicalStatus(item)
  return !!st && (BLOCKING_PHYSICAL_STATUSES as readonly string[]).includes(st)
}

/** Список операций для ReviewGate. */
export function buildReviewOperations(
  items: WorkOrderItemPreview[],
): { label: string; kind: ReviewKind }[] {
  return items.map(i => ({ label: i.description, kind: itemKind(i.item_type) }))
}

/** Конфликты физической валидации для баннера ReviewGate. */
export function collectConflicts(items: WorkOrderItemPreview[]): string[] {
  return items
    .filter(isPhysicallyBlocked)
    .map(i => {
      const reason = (i.metadata?.physical_reason as string | undefined) ?? physicalStatus(i)
      return `${i.description}: ${reason}`
    })
}

/**
 * Пересчёт has_unresolved после inline-резолва.
 * Item считается разрешённым, когда:
 *  - нет блокирующего physical_status,
 *  - для INSTALL: выбрана стойка (rack_id) и модель каталога (catalog_id),
 *  - для PATCH: выбран порт (port_id), если бэкенд прислал candidate_ports.
 */
export function recomputeHasUnresolved(item: WorkOrderItemPreview): boolean {
  const meta = item.metadata ?? {}
  if (isPhysicallyBlocked(item)) return true

  if (item.item_type === 'INSTALL_EQUIPMENT') {
    if (!meta.rack_id) return true
    // No catalog model at all is as unresolved as an inexact match: without it
    // execution would pick a catalog row by itself.
    if (!meta.catalog_id) return true
    return false
  }

  if (item.item_type === 'PATCH_CABLE') {
    const hasPort = !!(meta.port_id || meta.from_port_id || meta.to_port_id)
    const candidates = meta.candidate_ports as unknown[] | undefined
    if (candidates && candidates.length > 0 && !hasPort) return true
    return item.has_unresolved && !hasPort
  }

  if (item.item_type === 'REMOVE_EQUIPMENT' || item.item_type === 'UNPATCH_CABLE') {
    return !meta.equipment_id && item.has_unresolved
  }

  return item.has_unresolved
}

/** Применяет inline-правку к item и пересчитывает has_unresolved. */
export function applyResolution(
  item: WorkOrderItemPreview,
  patch: Record<string, unknown>,
): WorkOrderItemPreview {
  const merged: WorkOrderItemPreview = {
    ...item,
    metadata: { ...(item.metadata ?? {}), ...patch },
  }
  // Ручной выбор снимает физический конфликт исходного предложения AI:
  // если инженер поменял стойку/позицию/порт — старый вердикт больше не про этот вариант.
  if ('rack_id' in patch || 'position_u' in patch || 'port_id' in patch || 'catalog_id' in patch) {
    const meta = merged.metadata as Record<string, unknown>
    if (isPhysicallyBlocked(merged)) {
      meta.physical_status = 'UNVERIFIED'
      meta.physical_reason = 'Изменено вручную — будет проверено при выполнении'
    }
  }
  merged.has_unresolved = recomputeHasUnresolved(merged)
  return merged
}

/** Magic Prompt POST body — autonomy_level is the value enforce_autonomy_level sees. */
export function aiGenerateRequestBody(prompt: string, autonomyLevel: number) {
  return { prompt, autonomy_level: autonomyLevel }
}

/** true — создание наряда запрещено политикой автономии (advisory-only). */
export function isAdvisoryOnly(allowedAutonomyLevel?: number): boolean {
  return allowedAutonomyLevel === 1
}

/** Review Gate / create only when commit would not 409. Reason comes from the validator. */
export function canOpenReviewGate(commitBlockReason: string | null | undefined): boolean {
  return !commitBlockReason
}

/** 0 is Zero-U. Missing/null/'' → 1. `Number(x) || 1` would coerce 0 to 1. */
export function uHeightOrDefault(h: unknown): number {
  if (h == null || h === '') return 1
  const n = Number(h)
  return Number.isFinite(n) ? n : 1
}

export function physicsChanged(prev: WorkOrderItemPreview, next: WorkOrderItemPreview): boolean {
  const a = prev.metadata ?? {}
  const b = next.metadata ?? {}
  return (
    a.rack_id !== b.rack_id
    || a.position_u !== b.position_u
    || a.u_position !== b.u_position
    || a.catalog_id !== b.catalog_id
    || a.u_height !== b.u_height
    || a.port_id !== b.port_id
    || a.from_port_id !== b.from_port_id
    || a.to_port_id !== b.to_port_id
  )
}

export function jiraManualItems(description: string): WorkOrderItemPreview[] {
  const text = description.trim() || 'Jira'
  return [{ item_type: 'CUSTOM', description: text, metadata: {}, has_unresolved: false }]
}
